import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Zap, MapPin, Clock, Euro, CheckCircle, XCircle, Coffee, ShoppingBag } from 'lucide-react';
import './ChargingStationsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChargingStationsPage() {
  const { t } = useTranslation();
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/charging-stations`);
      setStations(response.data);
    } catch (error) {
      console.error('Error fetching charging stations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      available: '#16a34a',
      in_use: '#eab308',
      out_of_service: '#dc2626'
    };
    return colors[status] || '#6b7280';
  };

  const getConnectorIcon = (type) => {
    const icons = {
      'Type2': '⚡',
      'CCS': '🔌',
      'Schuko': '🔋'
    };
    return icons[type] || '⚡';
  };

  if (loading) {
    return (
      <div className="loading-container" data-testid="charging-loading">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="charging-page" data-testid="charging-page">
      <header className="page-header">
        <div>
          <h1 className="page-title" data-testid="charging-title">
            <Zap size={36} className="inline-icon" />
            E-Bike Ladestationen
          </h1>
          <p className="page-subtitle">Finde Ladestationen in ganz Schottland</p>
        </div>
      </header>

      <div className="stations-stats">
        <div className="stat-badge">
          <Zap size={20} />
          <span>{stations.length} Ladestationen</span>
        </div>
        <div className="stat-badge">
          <CheckCircle size={20} />
          <span>{stations.filter(s => s.status === 'available').length} Verfügbar</span>
        </div>
      </div>

      <div className="stations-grid">
        {stations.map((station) => (
          <div key={station.id} className="station-card" data-testid={`station-card-${station.id}`}>
            <div className="station-header">
              <div className="station-name-section">
                <span className="connector-icon">{getConnectorIcon(station.connector_type)}</span>
                <h3 className="station-name" data-testid="station-name">{station.name}</h3>
              </div>
              <div 
                className="station-status"
                style={{ background: getStatusColor(station.status) }}
                data-testid="station-status"
              >
                {station.status === 'available' && <CheckCircle size={16} />}
                {station.status === 'out_of_service' && <XCircle size={16} />}
                <span>
                  {station.status === 'available' ? 'Verfügbar' : 
                   station.status === 'in_use' ? 'In Benutzung' : 'Außer Betrieb'}
                </span>
              </div>
            </div>

            <div className="station-location">
              <MapPin size={18} />
              <span>{station.location}</span>
            </div>

            <div className="station-details">
              <div className="detail-item">
                <Zap size={18} />
                <div>
                  <span className="detail-label">Leistung</span>
                  <span className="detail-value">{station.power_kw} kW</span>
                </div>
              </div>

              <div className="detail-item">
                <span className="detail-icon">{getConnectorIcon(station.connector_type)}</span>
                <div>
                  <span className="detail-label">Anschluss</span>
                  <span className="detail-value">{station.connector_type}</span>
                </div>
              </div>

              <div className="detail-item">
                <Euro size={18} />
                <div>
                  <span className="detail-label">Kosten</span>
                  <span className="detail-value">
                    {station.cost === 'free' ? 'Kostenlos' : 'Kostenpflichtig'}
                  </span>
                </div>
              </div>
            </div>

            {station.opening_hours && (
              <div className="station-hours">
                <Clock size={16} />
                <span>{station.opening_hours}</span>
              </div>
            )}

            {station.amenities.length > 0 && (
              <div className="station-amenities">
                {station.amenities.map((amenity, idx) => (
                  <span key={idx} className="amenity-tag">
                    {amenity === 'cafe' && <Coffee size={14} />}
                    {amenity === 'shop' && <ShoppingBag size={14} />}
                    {amenity === 'shops' && <ShoppingBag size={14} />}
                    {amenity}
                  </span>
                ))}
              </div>
            )}

            <div className="station-operator">
              Betreiber: {station.operator}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
